import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ProjectSettings() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [activeTab, setActiveTab] = useState('settings');
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState({});
  const [types, setTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPagination, setAuditPagination] = useState({});
  const [auditPage, setAuditPage] = useState(1);

  const [showTypeModal, setShowTypeModal] = useState(false);
  const [typeForm, setTypeForm] = useState({ name: '', description: '' });
  const [typeEditId, setTypeEditId] = useState(null);

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', default_settings: '' });
  const [templateEditId, setTemplateEditId] = useState(null);

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleForm, setRoleForm] = useState({ name: '', description: '', permissions: '' });
  const [roleEditId, setRoleEditId] = useState(null);

  const loadSettings = useCallback(() => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/projects-settings`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null).then(d => { setSettings(d); if (d) setSettingsForm({ default_progress_method: d.default_progress_method || 'task_completion', auto_code_generation: !!d.auto_code_generation, code_prefix: d.code_prefix || 'PRJ', require_approval_for_milestones: !!d.require_approval_for_milestones, require_approval_for_deliverables: !!d.require_approval_for_deliverables, allow_timer_without_task: !!d.allow_timer_without_task, default_working_hours_per_day: d.default_working_hours_per_day || 8 }); }).catch(() => {});
  }, [companyId, API]);

  const loadTypes = useCallback(() => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/projects-settings/types`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setTypes(Array.isArray(d) ? d : [])).catch(() => {});
  }, [companyId, API]);

  const loadTemplates = useCallback(() => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/projects-settings/templates`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setTemplates(Array.isArray(d) ? d : [])).catch(() => {});
  }, [companyId, API]);

  const loadRoles = useCallback(() => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/projects-settings/roles`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setRoles(Array.isArray(d) ? d : [])).catch(() => {});
  }, [companyId, API]);

  const loadAudit = useCallback((page = 1) => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/projects-settings/audit?page=${page}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => { setAuditLogs(Array.isArray(d.data) ? d.data : (Array.isArray(d) ? d : [])); setAuditPagination(d.meta || d); })
      .catch(() => setAuditLogs([]));
  }, [companyId, API]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  useEffect(() => {
    if (activeTab === 'types') loadTypes();
    if (activeTab === 'templates') loadTemplates();
    if (activeTab === 'roles') loadRoles();
    if (activeTab === 'audit') loadAudit(1);
  }, [activeTab, loadTypes, loadTemplates, loadRoles, loadAudit]);

  const saveSettings = () => {
    fetch(`${API}/companies/${companyId}/projects-settings`, {
      method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(settingsForm)
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); }).then(() => { window.alert('Settings saved'); loadSettings(); }).catch(() => window.alert('Failed to save'));
  };

  // Types CRUD
  const saveType = () => {
    const method = typeEditId ? 'PUT' : 'POST';
    const url = typeEditId ? `${API}/companies/${companyId}/projects-settings/types/${typeEditId}` : `${API}/companies/${companyId}/projects-settings/types`;
    fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(typeForm) })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setShowTypeModal(false); setTypeEditId(null); setTypeForm({ name: '', description: '' }); loadTypes(); })
      .catch(() => window.alert('Failed to save'));
  };
  const deleteType = (id) => { if (!window.confirm('Delete this type?')) return; fetch(`${API}/companies/${companyId}/projects-settings/types/${id}`, { method: 'DELETE', headers: getAuthHeaders() }).then(r => { if (r.ok) loadTypes(); else window.alert('Cannot delete'); }); };

  // Templates CRUD
  const saveTemplate = () => {
    const method = templateEditId ? 'PUT' : 'POST';
    const url = templateEditId ? `${API}/companies/${companyId}/projects-settings/templates/${templateEditId}` : `${API}/companies/${companyId}/projects-settings/templates`;
    const body = { ...templateForm };
    if (body.default_settings) { try { body.default_settings = JSON.parse(body.default_settings); } catch { /* keep as string */ } }
    fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setShowTemplateModal(false); setTemplateEditId(null); setTemplateForm({ name: '', description: '', default_settings: '' }); loadTemplates(); })
      .catch(() => window.alert('Failed to save'));
  };
  const deleteTemplate = (id) => { if (!window.confirm('Delete this template?')) return; fetch(`${API}/companies/${companyId}/projects-settings/templates/${id}`, { method: 'DELETE', headers: getAuthHeaders() }).then(r => { if (r.ok) loadTemplates(); else window.alert('Cannot delete'); }); };

  // Roles CRUD
  const saveRole = () => {
    const method = roleEditId ? 'PUT' : 'POST';
    const url = roleEditId ? `${API}/companies/${companyId}/projects-settings/roles/${roleEditId}` : `${API}/companies/${companyId}/projects-settings/roles`;
    const body = { ...roleForm };
    if (body.permissions) { try { body.permissions = JSON.parse(body.permissions); } catch { /* keep */ } }
    fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setShowRoleModal(false); setRoleEditId(null); setRoleForm({ name: '', description: '', permissions: '' }); loadRoles(); })
      .catch(() => window.alert('Failed to save'));
  };
  const deleteRole = (id) => { if (!window.confirm('Delete this role?')) return; fetch(`${API}/companies/${companyId}/projects-settings/roles/${id}`, { method: 'DELETE', headers: getAuthHeaders() }).then(r => { if (r.ok) loadRoles(); else window.alert('Cannot delete'); }); };

  if (!companyId) return <div style={pageStyle}><p>Please select a company.</p></div>;

  const tabs = [
    { key: 'settings', label: '⚙️ General' },
    { key: 'types', label: '📋 Project Types' },
    { key: 'templates', label: '📄 Templates' },
    { key: 'roles', label: '👤 Roles' },
    { key: 'audit', label: '📜 Audit Log' },
  ];

  return (
    <div style={pageStyle}>
      <div style={{ marginBottom: '16px' }}>
        <button onClick={() => navigate('/company/projects')} style={linkBtn}>← Back to Overview</button>
        <h2 style={{ margin: '4px 0 0', fontSize: '1.4rem', fontWeight: 700 }}>⚙️ Project Settings</h2>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e2e8f0', marginBottom: '20px', overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem',
            fontWeight: activeTab === t.key ? 600 : 400, color: activeTab === t.key ? '#3b82f6' : '#64748b',
            borderBottom: activeTab === t.key ? '2px solid #3b82f6' : '2px solid transparent', marginBottom: '-2px', whiteSpace: 'nowrap',
          }}>{t.label}</button>
        ))}
      </div>

      {/* General Settings */}
      {activeTab === 'settings' && settings && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '500px' }}>
            <div>
              <label style={labelStyle}>Default Progress Method</label>
              <select value={settingsForm.default_progress_method} onChange={e => setSettingsForm({ ...settingsForm, default_progress_method: e.target.value })} style={inputStyle}>
                <option value="task_completion">Task Completion</option>
                <option value="manual">Manual</option>
                <option value="milestone_based">Milestone Based</option>
                <option value="weighted_tasks">Weighted Tasks</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Code Prefix</label>
              <input value={settingsForm.code_prefix} onChange={e => setSettingsForm({ ...settingsForm, code_prefix: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Default Working Hours/Day</label>
              <input type="number" value={settingsForm.default_working_hours_per_day} onChange={e => setSettingsForm({ ...settingsForm, default_working_hours_per_day: e.target.value })} style={inputStyle} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={settingsForm.auto_code_generation} onChange={e => setSettingsForm({ ...settingsForm, auto_code_generation: e.target.checked })} /> Auto Code Generation
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={settingsForm.require_approval_for_milestones} onChange={e => setSettingsForm({ ...settingsForm, require_approval_for_milestones: e.target.checked })} /> Require Approval for Milestones
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={settingsForm.require_approval_for_deliverables} onChange={e => setSettingsForm({ ...settingsForm, require_approval_for_deliverables: e.target.checked })} /> Require Approval for Deliverables
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={settingsForm.allow_timer_without_task} onChange={e => setSettingsForm({ ...settingsForm, allow_timer_without_task: e.target.checked })} /> Allow Timer Without Task
            </label>
            <button onClick={saveSettings} style={{ ...primaryBtn, alignSelf: 'flex-start' }}>Save Settings</button>
          </div>
        </div>
      )}

      {/* Project Types */}
      {activeTab === 'types' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
            <button onClick={() => { setTypeEditId(null); setTypeForm({ name: '', description: '' }); setShowTypeModal(true); }} style={primaryBtn}>+ Add Type</button>
          </div>
          <div style={{ display: 'grid', gap: '8px' }}>
            {types.map(t => (
              <div key={t.id} style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{t.name}</div>
                  {t.description && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{t.description}</div>}
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button onClick={() => { setTypeEditId(t.id); setTypeForm({ name: t.name, description: t.description || '' }); setShowTypeModal(true); }} style={smallBtn}>Edit</button>
                  <button onClick={() => deleteType(t.id)} style={{ ...smallBtn, color: '#ef4444' }}>Delete</button>
                </div>
              </div>
            ))}
            {types.length === 0 && <p style={{ color: '#94a3b8', textAlign: 'center' }}>No project types</p>}
          </div>
        </>
      )}

      {/* Templates */}
      {activeTab === 'templates' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
            <button onClick={() => { setTemplateEditId(null); setTemplateForm({ name: '', description: '', default_settings: '' }); setShowTemplateModal(true); }} style={primaryBtn}>+ Add Template</button>
          </div>
          <div style={{ display: 'grid', gap: '8px' }}>
            {templates.map(t => (
              <div key={t.id} style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{t.name}</div>
                  {t.description && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{t.description}</div>}
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button onClick={() => { setTemplateEditId(t.id); setTemplateForm({ name: t.name, description: t.description || '', default_settings: t.default_settings ? JSON.stringify(t.default_settings) : '' }); setShowTemplateModal(true); }} style={smallBtn}>Edit</button>
                  <button onClick={() => deleteTemplate(t.id)} style={{ ...smallBtn, color: '#ef4444' }}>Delete</button>
                </div>
              </div>
            ))}
            {templates.length === 0 && <p style={{ color: '#94a3b8', textAlign: 'center' }}>No templates</p>}
          </div>
        </>
      )}

      {/* Roles */}
      {activeTab === 'roles' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
            <button onClick={() => { setRoleEditId(null); setRoleForm({ name: '', description: '', permissions: '' }); setShowRoleModal(true); }} style={primaryBtn}>+ Add Role</button>
          </div>
          <div style={{ display: 'grid', gap: '8px' }}>
            {roles.map(r => (
              <div key={r.id} style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{r.name}</div>
                  {r.description && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{r.description}</div>}
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button onClick={() => { setRoleEditId(r.id); setRoleForm({ name: r.name, description: r.description || '', permissions: r.permissions ? JSON.stringify(r.permissions) : '' }); setShowRoleModal(true); }} style={smallBtn}>Edit</button>
                  <button onClick={() => deleteRole(r.id)} style={{ ...smallBtn, color: '#ef4444' }}>Delete</button>
                </div>
              </div>
            ))}
            {roles.length === 0 && <p style={{ color: '#94a3b8', textAlign: 'center' }}>No project roles</p>}
          </div>
        </>
      )}

      {/* Audit Log */}
      {activeTab === 'audit' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>User</th>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>Entity</th>
                <th style={thStyle}>Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map(l => (
                <tr key={l.id}>
                  <td style={tdStyle}>{new Date(l.created_at).toLocaleString()}</td>
                  <td style={tdStyle}>{l.user?.name || `User #${l.user_id}`}</td>
                  <td style={tdStyle}><span style={{ ...badge, backgroundColor: l.action === 'created' ? '#22c55e' : l.action === 'deleted' ? '#ef4444' : '#3b82f6' }}>{l.action}</span></td>
                  <td style={tdStyle}>{l.entity_type} #{l.entity_id}</td>
                  <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{typeof l.details === 'object' ? JSON.stringify(l.details) : (l.details || '-')}</td>
                </tr>
              ))}
              {auditLogs.length === 0 && <tr><td colSpan="5" style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No audit logs</td></tr>}
            </tbody>
          </table>
          {auditPagination.last_page > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginTop: '12px' }}>
              {Array.from({ length: auditPagination.last_page }, (_, i) => (
                <button key={i + 1} onClick={() => { setAuditPage(i + 1); loadAudit(i + 1); }} style={{ ...smallBtn, fontWeight: auditPage === i + 1 ? 700 : 400, color: auditPage === i + 1 ? '#3b82f6' : '#334155' }}>{i + 1}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Type Modal */}
      {showTypeModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px' }}>{typeEditId ? 'Edit Type' : 'New Project Type'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input placeholder="Type Name *" value={typeForm.name} onChange={e => setTypeForm({ ...typeForm, name: e.target.value })} style={inputStyle} />
              <textarea placeholder="Description" value={typeForm.description} onChange={e => setTypeForm({ ...typeForm, description: e.target.value })} style={{ ...inputStyle, minHeight: '50px' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowTypeModal(false)} style={cancelBtn}>Cancel</button>
              <button onClick={saveType} style={primaryBtn}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px' }}>{templateEditId ? 'Edit Template' : 'New Template'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input placeholder="Template Name *" value={templateForm.name} onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })} style={inputStyle} />
              <textarea placeholder="Description" value={templateForm.description} onChange={e => setTemplateForm({ ...templateForm, description: e.target.value })} style={{ ...inputStyle, minHeight: '50px' }} />
              <textarea placeholder='Default Settings (JSON)' value={templateForm.default_settings} onChange={e => setTemplateForm({ ...templateForm, default_settings: e.target.value })} style={{ ...inputStyle, minHeight: '50px', fontFamily: 'monospace', fontSize: '0.8rem' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowTemplateModal(false)} style={cancelBtn}>Cancel</button>
              <button onClick={saveTemplate} style={primaryBtn}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Role Modal */}
      {showRoleModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px' }}>{roleEditId ? 'Edit Role' : 'New Project Role'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input placeholder="Role Name *" value={roleForm.name} onChange={e => setRoleForm({ ...roleForm, name: e.target.value })} style={inputStyle} />
              <textarea placeholder="Description" value={roleForm.description} onChange={e => setRoleForm({ ...roleForm, description: e.target.value })} style={{ ...inputStyle, minHeight: '50px' }} />
              <textarea placeholder='Permissions (JSON array)' value={roleForm.permissions} onChange={e => setRoleForm({ ...roleForm, permissions: e.target.value })} style={{ ...inputStyle, minHeight: '50px', fontFamily: 'monospace', fontSize: '0.8rem' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowRoleModal(false)} style={cancelBtn}>Cancel</button>
              <button onClick={saveRole} style={primaryBtn}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1400px', margin: '0 auto' };
const cardStyle = { backgroundColor: '#fff', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e2e8f0', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' };
const tdStyle = { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };
const badge = { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.68rem', color: '#fff', fontWeight: 600, textTransform: 'capitalize' };
const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };
const labelStyle = { fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px' };
const primaryBtn = { padding: '8px 16px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' };
const cancelBtn = { padding: '8px 16px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' };
const smallBtn = { padding: '4px 8px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' };
const linkBtn = { background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.85rem', padding: 0 };
const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px', maxHeight: '85vh', overflowY: 'auto' };
